import { ChevronRight, Filter, ListFilter, Pill, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { TimelineEntryType } from '@snootsvet/shared'

import { Button } from '@/frontend/components/ui/button'
import { Toggle } from '@/frontend/components/ui/toggle'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/frontend/components/ui/tooltip'
import { cn } from '@/frontend/lib/utils'

const FILTER_OPTIONS = [
  { value: 'appointment', label: 'Appointments' },
  { value: 'communication', label: 'Messages' },
  { value: 'consultation', label: 'Consults' },
  { value: 'note', label: 'Notes' },
  { value: 'task', label: 'Tasks' },
  { value: 'treatment', label: 'Treatments' },
  { value: 'vitals', label: 'Vitals' },
] as const satisfies { value: TimelineEntryType; label: string }[]

export type PatientTimelineFilter = (typeof FILTER_OPTIONS)[number]['value']

const ALL_FILTERS = FILTER_OPTIONS.map((option) => option.value)
const CLINICAL_FILTERS: PatientTimelineFilter[] = ['consultation', 'note', 'treatment', 'vitals']
const FILTERS_STORAGE_KEY = 'patient-timeline-filters'

function isFilterValue(value: unknown): value is PatientTimelineFilter {
  return FILTER_OPTIONS.some((option) => option.value === value)
}

/** Restores selected timeline entry types, ignoring stale stored values. */
function loadTimelineFilters(): PatientTimelineFilter[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY) ?? 'null')
    if (!Array.isArray(parsed)) return ALL_FILTERS
    const filters = [...new Set(parsed.filter(isFilterValue))]
    return filters.length > 0 || parsed.length === 0 ? filters : ALL_FILTERS
  } catch {
    return ALL_FILTERS
  }
}

/** Persists the patient's selected timeline entry types on this device. */
export function usePatientTimelineFilters() {
  const [filters, setFilters] = useState<PatientTimelineFilter[]>(loadTimelineFilters)

  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters))
    } catch {
      // Keep filters in memory when browser storage is unavailable.
    }
  }, [filters])

  return { filters, setFilters }
}

/** Timeline filter control with all and clinical presets. */
export function PatientTimelineFilters({
  filters,
  onFiltersChange,
}: {
  filters: PatientTimelineFilter[]
  onFiltersChange: (filters: PatientTimelineFilter[]) => void
}) {
  const [showFilters, setShowFilters] = useState(false)
  const hiddenFilterCount = ALL_FILTERS.length - filters.length

  function toggleFilter(filter: PatientTimelineFilter) {
    onFiltersChange(
      filters.includes(filter) ? filters.filter((value) => value !== filter) : [...filters, filter]
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-headline-md">Timeline</h2>
        <div className="flex items-center">
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Show all timeline entries"
                  onClick={() => onFiltersChange(ALL_FILTERS)}
                >
                  <RotateCcw className="size-3.5" />
                </Button>
              }
            />
            <TooltipContent>Show all timeline entries</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Show clinical timeline entries"
                  onClick={() => onFiltersChange(CLINICAL_FILTERS)}
                >
                  <Pill className="size-3.5" />
                </Button>
              }
            />
            <TooltipContent>Show consultations, notes, treatments, and vitals</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className={cn(
                    'ml-2 gap-0.5',
                    hiddenFilterCount > 0 && 'bg-yellow/50 border-yellow hover:bg-yellow/75!'
                  )}
                >
                  {hiddenFilterCount > 0 ? (
                    <ListFilter className="mr-1.5" />
                  ) : (
                    <Filter className="mr-1.5" />
                  )}
                  Filter
                  <ChevronRight
                    className={cn('ml-1 transition-transform', showFilters && 'rotate-90')}
                  />
                </Button>
              }
            />
            <TooltipContent className="max-w-64 space-y-1.5">
              <p className="font-semibold">Timeline filters</p>
              <p>Click a type to show or hide it. Your selection is remembered.</p>
              <p>Option/Alt-click a type to show only that type.</p>
              <p>
                The <RotateCcw className="inline size-3" /> and <Pill className="inline size-3" />{' '}
                shortcuts reset to all entries or clinical-only.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {showFilters && (
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {FILTER_OPTIONS.map((option) => (
            <Toggle
              key={option.value}
              pressed={filters.includes(option.value)}
              onPressedChange={(_pressed, details) => {
                if ('altKey' in details.event && details.event.altKey) {
                  details.cancel()
                  onFiltersChange([option.value])
                  return
                }
                toggleFilter(option.value)
              }}
              variant="outline"
              size="sm"
              className="relative h-6 rounded-full px-3 text-xs font-semibold text-muted-foreground hover:bg-muted aria-pressed:hover:bg-yellow/75 aria-pressed:border-yellow aria-pressed:bg-yellow/50 aria-pressed:text-foreground"
            >
              {option.label}
            </Toggle>
          ))}
        </div>
      )}
    </div>
  )
}
